import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  useResponsive,
  useSafeExpoNavigation,
  useSafeFocusEffect,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { type HeaderIconItems, useHeaderLeft } from '../../../../components';
import { handleHardwareBackButtonPress } from './helpers';
import { giftCardConfirmationHeaderButtonsMessages as messages } from './useGiftCardConfirmationHeaderButtons.messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A small hook to conditionally render custom navigation header buttons.
 *
 * NOTE: We use custom back button navigation since this screen can be
 *       accessed from the gift card orders screen, which is located in a
 *       different stack, so the default `goBack` function won't work
 *       as expected.
 */
export const useGiftCardConfirmationHeaderButtons = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  // ─── Route Params ─────────────────────────────────────────────────────

  const { referrer } = useSafeRouteParams<{ referrer?: string }>();

  // ─── Navigation ───────────────────────────────────────────────────────

  const navigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const router = useSafeRouter();

  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore

  const shouldShowGiftCardOrdersBackButton = referrer === 'gift-card-orders';

  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToGiftCardsScreen = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.dismissTo('/gift');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'GiftCardsTab',
      params: { screen: 'GiftCards' },
      initial: true,
    });
  }, [isExpoRouterEnabled, router, navigation]);

  const navigateToGiftCardOrdersScreen = useCallback(() => {
    if (isInStoreOrdersEnabled) {
      if (isExpoRouterEnabled) {
        router?.navigate('/account/gift-cards');
        return;
      }

      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'GiftCards' },
      });

      return;
    }

    if (isExpoRouterEnabled) {
      router?.navigate({ pathname: '/account/orders', params: { tabId: 'gift-cards' } }); // prettier-ignore
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'AccountTab',
      params: {
        screen: 'Orders',
        params: { tabId: 'gift-cards' },
      },
    });
  }, [isInStoreOrdersEnabled, isExpoRouterEnabled, router, navigation]);

  const navigateToPreviousScreen = useCallback(() => {
    if (shouldShowGiftCardOrdersBackButton) {
      navigateToGiftCardOrdersScreen();

      return;
    }

    navigateToGiftCardsScreen();
  }, [
    navigateToGiftCardOrdersScreen,
    navigateToGiftCardsScreen,
    shouldShowGiftCardOrdersBackButton,
  ]);

  const overrideHardwareBackButtonPress = useCallback(() => {
    const { removeListener } = handleHardwareBackButtonPress(
      navigateToPreviousScreen,
    );

    return removeListener;
  }, [navigateToPreviousScreen]);

  // ─── Header Controls ─────────────────────────────────────────────────

  const headerLeftItems = useMemo<HeaderIconItems>(() => {
    return [
      {
        key: 'join-or-sign-in-screen-header-back-btn',
        icon: 'IconArrowLeft',
        accessibilityLabel: formatMessage(messages.backButtonA11yLabel),
        onPress: navigateToPreviousScreen,
      },
    ];
  }, [formatMessage, navigateToPreviousScreen]);

  const shouldHideControls = !currentBreakpoint.isXS;
  const headerLeftXS = useHeaderLeft({ items: headerLeftItems });
  const headerLeft = shouldHideControls ? renderNoHeaderLeft : headerLeftXS;

  // ─── Effects ─────────────────────────────────────────────────────────

  useSafeFocusEffect(overrideHardwareBackButtonPress);

  /**
   * Set header controls.
   */
  useEffect(() => {
    const setOptions = navigation?.setOptions ?? expoNavigation?.setOptions;

    setOptions?.({
      headerLeft,

      // NOTE: We use a custom back button, thus we need to hide the default one.
      headerBackVisible: false,
    });
  }, [headerLeft, navigation, expoNavigation]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A no-op headerLeft renderer that prevents the default `HeaderBackButton`
 * from mounting. `HeaderBackButton` from `@react-navigation/elements` v2
 * calls `useLocale` which doesn't exist in `@react-navigation/native` v6.
 */
function renderNoHeaderLeft() {
  return null;
}
